import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { listDashboardAlerts } from "../api/endpoints/dashboard"
import type { ReactElement } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { getAssignmentSummary } from "../api/endpoints/assignments"
import { getPerformanceTeamSummary } from "../api/endpoints/performance"
import { AppSidebar, TopBar } from "../components/ui"
import { useAuth } from "../stores/auth"
import type { PermissionSet } from "../types/auth"

function AccessDeniedState() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md rounded-xl border border-red-200 bg-white p-8 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-600">
          Access denied
        </p>
        <h2 className="mt-3 text-2xl font-bold text-slate-900">
          You do not have access to this resource.
        </h2>
        <p className="mt-3 text-sm text-slate-500">
          Your current role does not include this screen. Use the visible navigation items for the
          modules available to your account.
        </p>
      </div>
    </div>
  )
}

export function DashboardLayout() {
  const { user, isLoading, logout } = useAuth()
  const location = useLocation()
  const role = user?.role
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false)

  const assignmentBadgeQuery = useQuery({
    queryKey: ["sidebar-assignment-badge"],
    queryFn: () => getAssignmentSummary(),
    enabled: ["ADMIN", "OPS_MANAGER"].includes(role ?? ""),
  })

  const performanceBadgeQuery = useQuery({
    queryKey: ["sidebar-performance-badge"],
    queryFn: () => getPerformanceTeamSummary(),
    enabled: ["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"].includes(role ?? ""),
  })

  const alertBadgeQuery = useQuery({
    queryKey: ["sidebar-alert-badges", role],
    queryFn: () => listDashboardAlerts(),
    enabled: ["SUPER_ADMIN", "ADMIN"].includes(role ?? ""),
  })

  useEffect(() => {
    setMobileNavigationOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileNavigationOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [mobileNavigationOpen])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading session...</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate replace state={{ from: location.pathname }} to="/login" />
  }

  const alertItems = alertBadgeQuery.data?.data ?? []
  const pendingDisbursements =
    alertItems.find((item: any) => item.key === "pending_disbursements")?.count ?? 0
  const breachedTickets =
    alertItems.find((item: any) => item.key === "breached_tickets")?.count ?? 0
  const pendingKyc = alertItems.find((item: any) => item.key === "pending_kyc")?.count ?? 0

  const badgeCounts = {
    targets: assignmentBadgeQuery.data?.data?.badgeCounts?.unassignedClients ?? 0,
    agent_performance: performanceBadgeQuery.data?.data?.badgeCounts?.redAgents ?? 0,
    disbursements: pendingDisbursements,
    tickets: breachedTickets,
    verification: pendingKyc,
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-slate-50">
      <div className="hidden h-dvh lg:flex">
        <AppSidebar
          badgeCounts={badgeCounts}
          navigation={user.navigation ?? []}
          user={{ fullName: user.fullName, role: user.role }}
          onLogout={() => {
            void logout()
          }}
        />
      </div>

      {mobileNavigationOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className="fixed inset-0 z-40 flex bg-slate-950/45 p-3 backdrop-blur-sm lg:hidden"
        >
          <button
            type="button"
            aria-label="Close navigation menu"
            className="absolute inset-0 cursor-default border-0 bg-transparent"
            onClick={() => setMobileNavigationOpen(false)}
          />
          <div className="relative z-10 h-full w-[min(20rem,calc(100vw-1.5rem))] max-w-full">
            <AppSidebar
              mobile
              badgeCounts={badgeCounts}
              navigation={user.navigation ?? []}
              user={{ fullName: user.fullName, role: user.role }}
              onNavigate={() => setMobileNavigationOpen(false)}
              onLogout={() => {
                void logout()
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar
          user={{
            fullName: user.fullName,
            role: user.role,
            canReviewKyc: Boolean(user.permissions?.canReviewKyc),
          }}
          mobileNavigationOpen={mobileNavigationOpen}
          onMenuToggle={() => setMobileNavigationOpen((current) => !current)}
        />
        <main
          key={location.key}
          className="admin-safe-bottom min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50 p-4 animate-page-enter sm:p-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export function RequireRole({
  allowedRoles,
  children,
}: {
  allowedRoles: string[]
  children: ReactElement
}) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-slate-500">Checking permissions...</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate replace to="/login" />
  }

  if (!allowedRoles.includes(user.role)) {
    return <AccessDeniedState />
  }

  return children
}

type BooleanPermissionKey = {
  [K in keyof PermissionSet]: PermissionSet[K] extends boolean ? K : never
}[keyof PermissionSet]

export function RequirePermission({
  permission,
  children,
}: {
  permission: BooleanPermissionKey
  children: ReactElement
}) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-slate-500">Checking permissions...</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate replace to="/login" />
  }

  if (user.permissions?.[permission] !== true) {
    return <AccessDeniedState />
  }

  return children
}
